import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTitle,
  cssBorderRadius,
  cssColorValue,
  cssSpacing,
  screenSmMin,
  screenXsMax,
  semanticColors,
  IconDevices,
  IconGridApps,
  IconListSparkling,
  IconLocked,
  IconPayment,
  IconUser,
  cssGraphicSize,
} from '@spotify-internal/encore-web';
import React, { useState, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { ArticleCategoryMetadata, ParentCategory } from 'lib/sanity/types';
import { useUbiCategoryNavigation } from 'utils/ubi/pages/useUbiCategoryNavigation';

import { ArticleChildCategories } from './ChildCategories';
import { sortCategoriesByRanking } from './customCategoryOrder';

const StyledAccordion = styled(Accordion)`
  background-color: ${cssColorValue(semanticColors.backgroundElevatedBase)};
  border-radius: ${cssBorderRadius('larger-2')};
  flex-shrink: 0;
  height: fit-content;
  padding-block-end: 0;
  max-width: 343px;
  width: 100%;

  @media (min-width: ${screenSmMin}) {
    max-width: none;
  }
`;

const StyledAccordionItem = styled(AccordionItem)`
  border: 0;
  border-block-end: 1px solid ${cssColorValue(semanticColors.essentialSubdued)};

  &:last-child {
    border-block-end: 0;
  }
`;

const StyledAccordionTitle = styled(AccordionTitle)`
  & span {
    display: flex;
    align-items: center;
    font-size: 16px;
  }

  & svg {
    block-size: 16px;
    inline-size: 16px;
  }

  padding: ${cssSpacing('looser')};

  @media (max-width: ${screenXsMax}) {
    padding: ${cssSpacing('looser-2')};
  }
`;

const StyledAccordionContent = styled(AccordionContent)`
  && {
    padding-block: ${cssSpacing('looser-2')};

    border-block-start: 1px solid ${cssColorValue(semanticColors.essentialSubdued)};
    @media (min-width: ${screenSmMin}) {
      padding-block: ${cssSpacing('looser')};
      padding-inline: ${cssSpacing('looser')};
    }
  }
`;

const iconStyles = css`
  margin-right: ${cssSpacing('tighter-2')};
  @media (min-width: ${screenSmMin}) {
    margin-right: ${cssSpacing('base')};
    width: ${cssGraphicSize('larger', 'decorative')};
    height: ${cssGraphicSize('larger', 'decorative')};
  }
`;

const StyledIconPayment = styled(IconPayment)`
  ${iconStyles}
`;

const StyledIconUser = styled(IconUser)`
  ${iconStyles}
`;

const StyledIconListSparkling = styled(IconListSparkling)`
  ${iconStyles}
`;

const StyledIconGridApps = styled(IconGridApps)`
  ${iconStyles}
`;

const StyledIconDevices = styled(IconDevices)`
  ${iconStyles}
`;

const StyledIconLocked = styled(IconLocked)`
  ${iconStyles}
`;

const categoriesIcons: Record<string, ReactNode> = {
  'payment-help': <StyledIconPayment semanticColor="textBrightAccent" size="small" />,
  'account-help': <StyledIconUser semanticColor="textBrightAccent" size="small" />,
  'plan-help': <StyledIconListSparkling semanticColor="textBrightAccent" size="small" />,
  'app-help': <StyledIconGridApps semanticColor="textBrightAccent" size="small" />,
  'device-help': <StyledIconDevices semanticColor="textBrightAccent" size="small" />,
  'safety-privacy': <StyledIconLocked semanticColor="textBrightAccent" size="small" />,
};

export const ArticleCategories = ({
  categories,
  categoryMetadata,
  withIcons = false,
  ...props
}: {
  categories: ParentCategory[];
  categoryMetadata?: ArticleCategoryMetadata | null;
  withIcons?: boolean;
}) => {
  const sortedCategories = categories.sort(sortCategoriesByRanking);

  const indexOfParentCategory = sortedCategories.findIndex(
    ({ slug }) => slug === categoryMetadata?.parentCategory?.slug,
  );
  const indexOfChildCategory =
    indexOfParentCategory === -1
      ? -1
      : sortedCategories[indexOfParentCategory].childCategories.findIndex(
          ({ slug }) => slug === categoryMetadata?.slug,
        );

  const [activeIndex, updateActive] = useState(indexOfParentCategory);
  const [childIndex, updateChildIndex] = useState(indexOfChildCategory);
  const ubi = useUbiCategoryNavigation();

  return (
    <StyledAccordion aria-label="Accordion group for article categories" {...props}>
      {sortedCategories
        .filter(({ title }) => title !== null)
        .map(({ slug, title, childCategories }, index) => (
          <StyledAccordionItem key={slug}>
            <StyledAccordionTitle
              onClick={e => {
                e.preventDefault();
                ubi.logParentCategoryTitleInteraction({ expanded: index !== activeIndex, identifier: slug });
                updateActive(index === activeIndex ? -1 : index);
                updateChildIndex(-1);
              }}
              isExpanded={index === activeIndex}
            >
              {withIcons && categoriesIcons[slug]}
              {title}
            </StyledAccordionTitle>
            <StyledAccordionContent isExpanded={index === activeIndex}>
              <ArticleChildCategories
                parentSlug={slug}
                categories={childCategories}
                initialIndex={index === activeIndex ? childIndex : -1}
              />
            </StyledAccordionContent>
          </StyledAccordionItem>
        ))}
    </StyledAccordion>
  );
};
